import React from 'react';

const PrivacyPolicy: React.FC = () => {
    return (
        <div className="flex flex-col items-center px-6 text-[#333] md:pt-10 pt-[73px] h-auto">
            {/* Section 1 */}
            <h1 className="md:text-5xl text-4xl md:text-6xl font-light mb-10">Privacy Policy</h1>
            <div className='space-y-4 mb-4 w-full'>
                <h2 className="text-[#3A57FF] font-medium text-[26px]">What this policy covers</h2>
                <p className='text-[17px]'>
                    This Privacy Policy explains how we collect and use your information when you use our products, services,
                    websites, or interact with us. It applies to all Ultimate Boards (UBOARDS INNOVATIONS LTD) offerings, including cloud, server, and
                    data center products. It also outlines your choices, like accessing or updating your data and objecting to
                    certain uses. If you don’t agree, please don’t use our Services. When Services are provided under a contract
                    with an organization, that organization controls the data, and this policy doesn’t apply to that processing.
                </p>
            </div>
            {/* Section 2 */}
            <div className='space-y-4 mb-4 w-full'>
                <h2 className="text-[#3A57FF] font-medium text-[26px]">What information we collect about you</h2>
                <div className="space-y-4 text-[17px]">
                    <div>
                        <p className="font-semibold">Information You Provide:</p>
                        <p>We collect data you enter into our services or share directly with us.</p>
                    </div>
                    <div>
                        <p className="font-semibold">Account & Profile Details:</p>
                        <p>
                            When you register, update your profile, or make purchases, we gather contact, billing, and optional profile info like
                            name, photo, and job title. We also track your settings and preferences.
                        </p>
                    </div>
                    <div>
                        <p className="font-semibold">Content You Provide:</p>
                        <p>
                            We collect and store content you create or share through our products. This may include personal information you choose
                            to include. For server or data center versions, we generally don’t collect content unless allowed by your administrator—
                            for example, feedback, anonymized analytics, or usage data. Admins can disable or block this data collection.
                        </p>
                    </div>
                    <div>
                        <p className="font-semibold">Website & Support Content:</p>
                        <p>
                            We collect content you submit on our websites, including feedback, surveys, contests, and event participation. We also
                            gather info you share through support channels—like contact details, issue summaries, and related files or screenshots.
                        </p>
                    </div>
                    <div>
                        <p className="font-semibold">Payment Info:</p>
                        <p>
                            For paid services, we collect billing details, including contact info and payment card data, through secure processors.
                        </p>
                    </div>
                </div>
            </div>
            {/* Section 3 */}
            <div className='space-y-4 mb-4 w-full'>
                <h2 className="text-[#3A57FF] font-medium text-[26px]">Information we collect automatically when you use the Services</h2>
                <div className="space-y-4 text-[17px]">
                    <div>
                        <p>We collect information when you browse our sites or interact with our Services.</p>
                    </div>
                    <div>
                        <p className="font-semibold">Service Usage:</p>
                        <p>
                            We track how you interact with our Services—features used, links clicked, files uploaded, search terms, and collaboration patterns. For server or data center versions, we only collect limited usage data unless allowed by your administrator.
                        </p>
                    </div>
                    <div>
                        <p className="font-semibold">Device & Connection Info:</p>
                        <p>
                            We collect data about the devices you use, like OS, browser, IP address, crash reports, and connection settings. This helps us improve your experience. Data collection may vary by device type and can be disabled by server or data center admins.
                        </p>
                    </div>
                    <div>
                        <p className="font-semibold">Cookies & Tracking:</p>
                        <p>
                            We and our partners use cookies and similar technologies to enable features and recognize you across devices. For details and opt-out options, see our Cookies and Tracking Notice.
                        </p>
                    </div>
                </div>
            </div>
            {/* Section 4 */}
            <div className='space-y-4 mb-4 w-full'>
                <h2 className="text-[#3A57FF] font-medium text-[26px]">Information we receive from other sources</h2>
                <div className="space-y-4 text-[17px]">
                    <div>
                        <p>We receive data about you from other users, partners, related companies, public databases, and social media. We may combine it with other data to improve records, personalize ads, and suggest relevant services.</p>
                    </div>
                    <div>
                        <p className="font-semibold">Info from Other Users:</p>
                        <p>
                            Other users may share information about you—such as mentioning you in UBOARDS INNOVATIONS LTD work items, uploading content about you, or inviting you via email. Admins may also provide your contact details when assigning roles like billing or technical support.
                        </p>
                    </div>
                    <div>
                        <p className="font-semibold">Linked Services:</p>
                        <p>
                            We receive information when you or your admin connect third-party apps to your account—like signing in with Google or syncing calendars, contacts, or files. These integrations may share data based on their settings and privacy policies. Always review third-party privacy notices to understand what’s shared with us.
                        </p>
                    </div>
                    <div>
                        <p className="font-semibold">Ultimate Boards Affiliates and Partners:</p>
                        <p>
                            We receive information about you from UBOARDS INNOVATIONS LTD’s corporate affiliates, as allowed by their policies. Our global network of UBOARDS INNOVATIONS LTD partners may share details like billing contacts, company name, services of interest, event attendance, and location to help us deliver, promote, and improve our Services.
                        </p>
                    </div>
                    <div>
                        <p className="font-semibold">Other Partners & Providers:</p>
                        <p>
                            We also receive data from third-party partners—such as advertisers, market researchers, and public sources like social media. This may include contact info, job titles, IP addresses, and engagement data to support personalized communication, targeted ads, and event outreach.
                        </p>
                    </div>
                </div>
            </div>
            {/* Section 5 */}
            <div className='space-y-4 mb-4 w-full'>
                <h2 className="text-[#3A57FF] font-medium text-[26px]">How we use information we collect</h2>
                <div className="space-y-4 text-[17px]">
                    <div>
                        <p>How we use your data depends on the UBOARDS INNOVATIONS LTD Services you use and your preferences. Here's how we apply the information we collect:</p>
                    </div>
                    <div>
                        <p className="font-semibold">To deliver and personalize the Services:</p>
                        <p>
                            We use your data to process transactions, verify your identity, provide support, and improve our Services. This includes customizing your experience—like showing relevant search results, notifications, and recommendations based on your role, activity, and team interactions.
                        </p>
                    </div>
                    <div>
                        <p className="font-semibold">To enhance collaboration and productivity:</p>
                        <p>
                            Your profile info (e.g. name, photo, job title) helps others identify and connect with you. We may also infer your organization or industry from your email domain to tailor content and features.
                        </p>
                    </div>
                    <div>
                        <p className="font-semibold">To create a seamless experience across UBOARDS INNOVATIONS LTD Services:</p>
                        <p>
                            If you use multiple UBOARDS INNOVATIONS LTD tools, we combine your data to help you find information across platforms and receive relevant updates and offers.
                            If you’d prefer not to receive personalized content or communications, you can opt out by contacting us.
                        </p>
                    </div>
                    <div>
                        <p className="font-semibold">Research & Development:</p>
                        <p>
                            We use data and user feedback to improve UBOARDS INNOVATIONS LTD Services—making them smarter, faster, and more useful. This includes analyzing usage patterns, search terms, and interactions to enhance features like mentions and search suggestions. We also test new tools with select users before wider release and apply insights across products to improve integration and user experience.
                        </p>
                    </div>
                    <div>
                        <p className="font-semibold">Communicating with You About the Services:</p>
                        <p>
                            We use your contact details to send transactional messages—such as purchase confirmations, subscription reminders, responses to support requests, and technical or security updates—via email and within the Services. You’ll receive notifications when others interact with you, like being mentioned or assigned a task. Based on your activity, we may suggest features or third-party apps to help streamline your workflow. During onboarding, we provide helpful guidance to improve your experience with the Services. These communications are essential to the Services and typically cannot be opted out of. If an opt-out is available, you’ll find it in the message or your account settings.
                        </p>
                    </div>
                    <div>
                        <p className="font-semibold">Marketing, Promotion & Engagement:</p>
                        <p>
                            We use your contact details and usage data to send promotional messages—via email or ads on other websites—that may be relevant to you. These include updates on new features, surveys, newsletters, events, product offers, and contests. We also analyze interactions to improve the effectiveness of these communications and help you get more value from the Services. You can manage your preferences as explained in the “Opt-out of communications” section.
                        </p>
                    </div>
                    <div>
                        <p className="font-semibold">Customer Support:</p>
                        <p>
                            We use your information to troubleshoot issues, respond to support requests, analyze crashes, and improve the Services. With your explicit permission, we may share details with third-party experts to assist with support.
                        </p>
                    </div>
                    <div>
                        <p className="font-semibold">Safety & Security:</p>
                        <p>
                            We monitor account activity and Service usage to verify identities, detect and prevent security threats, and guard against fraud, deception, and policy violations.
                        </p>
                    </div>
                    <div>
                        <p className="font-semibold">Legal & Business Interests:</p>
                        <p>
                            We use your data to meet legal obligations, manage audits and compliance, and protect our rights and interests. This may include disclosures related to mergers, acquisitions, or business sales.
                        </p>
                    </div>
                    <div>
                        <p className="font-semibold">With Your Consent:</p>
                        <p>
                            When you give us permission, we use your information for specific purposes not covered above—such as publishing testimonials or customer stories to promote the Services.
                        </p>
                    </div>
                    <div>
                        <p className="font-semibold">Legal Bases for Processing (EEA Users):</p>
                        <p>
                            If you're located in the European Economic Area (EEA), we only collect and process your data when we have a valid legal basis under EU law. This depends on the Services you use and how you use them. We process your information when:
                        </p>
                        <ul className="list-disc list-inside">
                            <li>It’s necessary to deliver the Services, including support, personalization, and security</li>
                            <li>We have a legitimate interest that doesn’t override your data protection rights (e.g. for development, marketing, or legal protection)</li>
                            <li>You’ve given consent for a specific purpose</li>
                            <li>We’re required to comply with legal obligations</li>
                        </ul>
                        <p>
                            If you’ve given consent, you can withdraw it at any time—though this won’t affect processing already completed. If we rely on legitimate interest (ours or a third party’s, like your employer), you can object, but doing so may limit your ability to use the Services.
                        </p>
                    </div>
                </div>
            </div>
            {/* Section 6 */}
            <div className='space-y-4 mb-4 w-full'>
                <h2 className="text-[#3A57FF] font-medium text-[26px]">How we use information we collect</h2>
                <div className="space-y-4 text-[17px]">
                    <div>
                        <p>To support collaboration and ensure our Services function effectively, we share information through the platform and with select third parties. This may include disclosures related to business transfers. Data collected via third-party cookies, pixels, or similar technologies—used for cross-context behavioral advertising—may be classified as a “sale” or “share” under certain international or country-specific laws. However, we do not sell personal information for monetary gain and are not a data broker.</p>
                    </div>
                    <div>
                        <p className="font-semibold">Sharing with Other Service Users:</p>
                        <p>
                            When you use UBOARDS INNOVATIONS LTD Services, certain information about you may be visible to other users. For collaboration, content you create or interact with may include your profile details—such as name and photo—based on permissions set by you or your administrator. For example, when you comment or publish content, your name may appear alongside it so others know who contributed. Depending on team roles and settings, your activity or profile info may be visible to teammates. Some areas of UBOARDS INNOVATIONS LTD Services can be made public, meaning posted content—including your information—may be indexed by search engines. You can check visibility settings within the Services or consult your administrator.
                        </p>
                    </div>
                    <div>
                        <p className="font-semibold">Managed Accounts and Administrators:</p>
                        <p>
                            If you sign up or access UBOARDS INNOVATIONS LTD Services using an email tied to your organization’s domain, your account may be linked to that organization. This means your name, profile photo, contact details, content, and usage history may be visible to the organization’s administrator and other users with the same domain. If you're an administrator for a site or user group within UBOARDS INNOVATIONS LTD Services, your contact information may be shared with current or former users to help manage service-related requests.
                        </p>
                    </div>
                    <div>
                        <p className="font-semibold">Community Forums:</p>
                        <p>
                            UBOARDS INNOVATIONS LTD websites may include public blogs, forums, and issue trackers. Any information you share—such as posts and profile details linked to your account—can be viewed, collected, and used by anyone who accesses these sites. Even if you close your account, your posts and some profile information may remain visible. Please be mindful of what you share, especially if it's sensitive. If you’d like your information removed from publicly accessible UBOARDS INNOVATIONS LTD websites, contact us using the details provided below. In some cases, removal may not be possible, and we’ll inform you if and why that’s the case.
                        </p>
                    </div>
                    <div>
                        <p className="font-semibold">Disclosure to Third Parties:</p>
                        <p>
                            We share information with third parties who help us operate, improve, support, and market UBOARDS INNOVATIONS LTD Services. This excludes any text messaging opt-in data and consent, which will not be shared with third parties—except for aggregators and providers of messaging services.
                        </p>
                    </div>
                    <div>
                        <p className="font-semibold">Service Providers:</p>
                        <p>
                            We engage third-party providers for tasks like website and app development, hosting, maintenance, storage, infrastructure, payment processing, analytics, and marketing. These providers may need access to your information to perform services on our behalf. When they do, they follow our strict instructions and adhere to security and confidentiality standards.
                        </p>
                    </div>
                    <div>
                        <p className="font-semibold">Partners and Consultants:</p>
                        <p>
                            We also work with third parties who offer consulting, sales, support, and technical services to help deliver and customize UBOARDS INNOVATIONS LTD Services. Your information may be shared with them to assist with billing, provide localized support, or implement custom solutions. We only disclose your information to these partners when necessary or when you’ve agreed to it.
                        </p>
                    </div>
                    <div>
                        <p className="font-semibold">Third-Party Apps:</p>
                        <p>
                            UBOARDS INNOVATIONS LTD Service users may install third-party apps to add features or customize functionality. These apps may access your account information—such as your name, email address, and any content you use with the app. If you're listed as an administrator or billing contact, your details may be shared with the app provider during installation. Please note that third-party apps operate independently, and their privacy policies are not governed by UBOARDS INNOVATIONS LTD. We recommend reviewing their policies before use. If you do not want your information shared, you should uninstall the app.
                        </p>
                    </div>
                    <div>
                        <p className="font-semibold">Links to Third-Party Sites:</p>
                        <p>
                            UBOARDS INNOVATIONS LTD Services may contain links to external websites or services with different privacy practices. Any information you submit to those sites is subject to their privacy policies, not ours. We encourage you to read those policies carefully.
                        </p>
                    </div>
                    <div>
                        <p className="font-semibold">Third-Party Services:</p>
                        <p>
                            UBOARDS INNOVATIONS LTD Services may allow you to interact with or share information with third parties—for example, by logging in through external services or accessing embedded content. When you engage with these services, we may share or receive information such as contact details, identifiers, and device data, depending on your privacy settings. Always review the privacy settings and notices of these third-party services to understand how your information may be used.
                        </p>
                    </div>
                    <div>
                        <p className="font-semibold">Third-Party Widgets:</p>
                        <p>
                            Some UBOARDS INNOVATIONS LTD Services include widgets or social media features that may collect your IP address, track which pages you visit, and set cookies to function properly. These features may be hosted by third parties or directly within UBOARDS INNOVATIONS LTD Services. Always review the privacy policies of these third-party services to understand how your data may be used.
                        </p>
                    </div>
                    <div>
                        <p className="font-semibold">With Your Consent:</p>
                        <p>
                            We share your information with third parties when you’ve given explicit consent. For example, we may publish customer testimonials on our public websites and include your name if you’ve agreed to it.
                        </p>
                    </div>
                    <div>
                        <p className="font-semibold">Legal Compliance & Protection:</p>
                        <p>
                            In rare cases, we may disclose your information if necessary to:
                        </p>
                        <ul className="list-disc list-inside">
                            <li>Comply with laws, regulations, or legal requests</li>
                            <li>Enforce our terms, policies, or agreements</li>
                            <li>Protect the security and integrity of UBOARDS INNOVATIONS LTD Services</li>
                            <li>Prevent harm or illegal activity affecting UBOARDS INNOVATIONS LTD, users, or the public</li>
                            <li>Respond to emergencies involving potential serious injury or loss of life</li>
                        </ul>
                        <p>
                            For details on how we respond to government requests, please contact us directly.
                        </p>
                    </div>
                </div>
            </div>
            {/* Section 7 */}
            <div className='space-y-4 mb-4 w-full'>
                <h2 className="text-[#3A57FF] font-medium text-[26px]">Disclosure to Affiliated Companies</h2>
                <div className="space-y-4 text-[17px]">
                    <div>
                        <p>UBOARDS INNOVATIONS LTD may share your information with affiliated companies—those we own or operate—as well as with prospective affiliates. These disclosures are governed by the same protections outlined in this privacy policy.</p>
                    </div>
                    <div>
                        <p className="font-semibold">UBOARDS INNOVATIONS LTD Corporate Affiliates</p>
                        <p>
                            We may share your information with other UBOARDS INNOVATIONS LTD corporate entities to help operate and improve our products and services, and to offer additional UBOARDS INNOVATIONS LTD-affiliated services. This includes companies that own or manage parts of the UBOARDS INNOVATIONS LTD platform.
                        </p>
                    </div>
                    <div>
                        <p className="font-semibold">Business Transfers</p>
                        <p>
                            If UBOARDS INNOVATIONS LTD undergoes a merger, acquisition, asset sale, or financing event, your information may be transferred or disclosed as part of that transaction. If such a change occurs, we'll notify you via email and/or a prominent notice on our Services, along with any options you may have regarding your data.
                        </p>
                    </div>
                </div>
            </div>

            {/* Section 8 */}
            <div className='space-y-4 mb-4 w-full'>
                <h2 className="text-[#3A57FF] font-medium text-[26px]">How We Store and Secure Information</h2>
                <div className="space-y-4 text-[17px]">
                    <div>
                        <p className="font-semibold">Information Storage & Security</p>
                        <p>
                            UBOARDS INNOVATIONS LTD takes data protection seriously and implements safeguards to help secure your information. However, no system is completely immune to risks. Due to the nature of the Internet, we cannot guarantee that data transmitted online or stored on our systems is entirely safe from unauthorized access or intrusion.
                        </p>
                    </div>
                    <div>
                        <p className="font-semibold">Server & Data Center Responsibility</p>
                        <p>
                            If you use UBOARDS INNOVATIONS LTD server or data center Services, you are responsible for securing the information you store and access through those platforms. We strongly recommend:
                        </p>
                        <ul className="list-disc list-inside">
                            <li>Enabling SSL encryption to protect data in transit</li>
                            <li>Restricting access to databases and storage systems</li>
                            <li>Following best practices for network and infrastructure security</li>
                        </ul>
                        <p>
                            UBOARDS INNOVATIONS LTD provides tools and guidance, but ultimate responsibility for securing your environment lies with you.
                        </p>
                    </div>
                </div>
            </div>

            {/* Section 9 */}
            <div className='space-y-4 mb-4 w-full'>
                <h2 className="text-[#3A57FF] font-medium text-[26px]">How Long We Keep Your Information</h2>
                <div className="space-y-4 text-[17px]">
                    <div>
                        <p>The duration for which UBOARDS INNOVATIONS LTD retains your information depends on its type and purpose. After the retention period, we either delete or de-identify your data. If deletion isn't immediately possible (e.g., due to backup systems), we securely store and isolate the data until it can be removed.</p>
                    </div>
                    <div>
                        <p className="font-semibold">Account Information</p>
                        <p>
                            We retain your account data:
                        </p>
                        <ul className="list-disc list-inside">
                            <li>As long as your account is active</li>
                            <li>For a reasonable time afterward, in case you reactivate</li>
                            <li>As needed to comply with legal obligations, resolve disputes, enforce agreements, support operations, and improve our Services</li>
                        </ul>
                        <p>
                            For service improvement, we anonymize personal identifiers and use the data only to generate collective insights—not to analyze individual behavior.
                        </p>
                    </div>
                    <div>
                        <p className="font-semibold">Information You Share</p>
                        <p>
                            Even if your account is deactivated or disabled, content you've shared (e.g., messages, files) may remain accessible to other users. We preserve this to maintain continuity for teams and collaborators. Upon request, we will remove identifying details where feasible.
                        </p>
                    </div>
                    <div>
                        <p className="font-semibold">Managed Accounts</p>
                        <p>
                            If your access to UBOARDS INNOVATIONS LTD Services is provided through an organization (like your employer), your data is retained according to the organization's administrator settings. See "Managed Accounts and Administrators" for more details.
                        </p>
                    </div>
                    <div>
                        <p className="font-semibold">Marketing Information</p>
                        <p>
                            If you've opted into marketing communications:
                        </p>
                        <ul className="list-disc list-inside">
                            <li>We retain your preferences for a reasonable time after your last interaction (e.g., opening an email or using your UBOARDS INNOVATIONS LTD account)</li>
                            <li>Data from cookies and tracking technologies is retained for a reasonable time from its creation</li>
                        </ul>
                    </div>
                </div>
            </div>

            {/* Section 10 */}
            <div className='space-y-4 mb-4 w-full'>
                <h2 className="text-[#3A57FF] font-medium text-[26px]">How to Access and Control Your Information</h2>
                <div className="space-y-4 text-[17px]">
                    <div>
                        <p>You have choices regarding how your personal information is used. These rights may vary depending on your location and local laws.</p>
                    </div>
                    <div>
                        <p className="font-semibold">Your Rights May Include</p>
                        <ul className="list-disc list-inside">
                            <li>Requesting information about how UBOARDS INNOVATIONS LTD processes your data</li>
                            <li>Accessing or downloading a copy of your information</li>
                            <li>Correcting or updating your personal details</li>
                            <li>Requesting deletion or restriction of your data</li>
                            <li>Objecting to data use (including for marketing)</li>
                            <li>Receiving your data in a structured, electronic format</li>
                            <li>Opting out of "sales," "sharing," or targeted advertising</li>
                            <li>Opting out of certain profiling activities</li>
                        </ul>
                    </div>
                    <div>
                        <p className="font-semibold">How to Exercise Your Rights:</p>
                        <ul className="list-disc list-inside">
                            <li>Use account or Service settings to manage your data directly</li>
                            <li>If your account is managed by an organization, contact your administrator first</li>
                            <li>For other requests, reach out via the Contact Us section</li>
                        </ul>
                    </div>
                    <div>
                        <p className="font-semibold">Limitations:</p>
                        <p>
                            Some requests may be restricted—for example, if they affect another person's privacy, conflict with legal obligations, or involve data UBOARDS INNOVATIONS LTD or your administrator must retain for legitimate reasons. If you've shared data with third-party apps, you'll need to contact those providers directly to manage or delete that information.
                        </p>
                    </div>
                    <div>
                        <p className="font-semibold">Unresolved Concerns:</p>
                        <p>
                            If you believe your rights have been violated, you may have the right to file a complaint with a data protection authority in your country of residence, employment, or where the issue occurred. You may also appeal any decision UBOARDS INNOVATIONS LTD makes regarding your data rights by contacting us as outlined in the Contact Us section.
                        </p>
                    </div>
                </div>
            </div>
            {/* Section 11 */}
            <div className='space-y-4 mb-4 w-full'>
                <h2 className="text-[#3A57FF] font-medium text-[26px]">Managing Your Information</h2>
                <div className="space-y-4 text-[17px]">
                    <div>
                        <p>You have control over your personal data within our Services. Here’s how you can access, update, deactivate, or delete your information:</p>
                    </div>
                    <div>
                        <p className="font-semibold">Access & Update Your Information</p>
                        <ul className="list-disc list-inside">
                            <li>Use your account settings to view and edit your profile details</li>
                            <li>Search for content related to you using keyword search within the Service</li>
                            <li>Edit or remove personal content using built-in editing tools</li>
                            <li>Refer to product-specific help guides:
                                <ul className="list-disc list-inside ml-6">
                                    <li>UBOARDS INNOVATIONS LTD Documentation</li>
                                    <li>UBOARDS INNOVATIONS LTD Help</li>
                                    <li>Loom Documentation</li>
                                </ul>
                            </li>
                        </ul>
                    </div>
                    <div>
                        <p className="font-semibold">Deactivate Your Account</p>
                        <ul className="list-disc list-inside">
                            <li>If self-deactivation is available, you’ll find the option in your account settings</li>
                            <li>If your account is managed by an organization, contact your administrator</li>
                            <li>Administrators who need help can reach out via UBOARDS INNOVATIONS LTD Support</li>
                        </ul>
                    </div>
                    <div>
                        <p className="font-semibold">Delete Your Information</p>
                        <ul className="list-disc list-inside">
                            <li>Remove personal content using search and editing tools</li>
                            <li>Delete profile details via your profile settings</li>
                            <li>Some data may be retained for:
                                <ul className="list-disc list-inside ml-6">
                                    <li>Legal compliance</li>
                                    <li>Transaction completion</li>
                                    <li>Record-keeping purposes</li>
                                </ul>
                            </li>
                        </ul>
                    </div>
                </div>
            </div>
            {/* Section 12 */}
            <div className='space-y-4 mb-4 w-full'>
                <h2 className="text-[#3A57FF] font-medium text-[26px]">Request to Stop Use of Your Information</h2>
                <div className="space-y-4 text-[17px]">
                    <div>
                        <p>If you believe we’re using your data without proper rights—or you’re no longer an active user—you can request that we stop processing your information.</p>
                    </div>
                    <div>
                        <p className="font-semibold">What You Can Do:</p>
                        <ul className="list-disc list-inside">
                            <li>Request deletion of an account created without your consent</li>
                            <li>Withdraw consent for specific uses (note: this won’t affect past processing)</li>
                            <li>Opt out of marketing by contacting us directly</li>
                            <li>If there’s a dispute or delay, we’ll restrict further use of your data until resolved—unless your administrator objects</li>
                        </ul>
                    </div>
                    <div>
                        <p className="font-semibold">Opt Out of Communications</p>
                        <p>You can stop receiving promotional messages from us through:</p>
                        <ul className="list-disc list-inside">
                            <li>The unsubscribe link in our emails</li>
                            <li>Email preferences in your account settings</li>
                            <li>Contacting us to remove your info from our promotional lists</li>
                        </ul>
                        <p className="mt-2">If we plan to use your data in new ways or share it with external parties not covered in this policy, you’ll be given a chance to opt out.</p>
                    </div>
                </div>
            </div>
            {/* Section 13 */}
            <div className='space-y-4 mb-4 w-full'>
                <h2 className="text-[#3A57FF] font-medium text-[26px]">Opt Out of Targeted Advertising</h2>
                <div className="space-y-4 text-[17px]">
                    <div>
                        <p>Opt Out of Targeted Advertising</p>
                        <p className="font-semibold">Where permitted by law, you can opt out of personalized ads:</p>
                        <ul className="list-disc list-inside">
                            <li>Use the opt-out link provided in your local settings</li>
                        </ul>
                    </div>

                    <div>
                        <p>Tracking Preferences & Do Not Track Signals</p>
                        <p>You have tools to control how your data is tracked across websites</p>
                    </div>

                    <div>
                        <p>Here's a refined and user-friendly version of that section, organized into two clear parts: tracking preferences and data portability.</p>
                        <p>Tracking Preferences & Do Not Track Signals</p>
                    </div>

                    <div>
                        <p className="font-semibold">You have tools to control how your data is tracked across websites:</p>
                        <p className="font-semibold">Do Not Track (DNT)</p>
                        <ul className="list-disc list-inside">
                            <li>Some browsers offer a DNT signal to indicate you don't want to be tracked</li>
                            <li>Our websites may not respond to all DNT signals</li>
                        </ul>
                    </div>

                    <div>
                        <p className="font-semibold">Global Privacy Control (GPC)</p>
                        <ul className="list-disc list-inside">
                            <li className="font-semibold">We do respond to the GPC signal to opt out of:
                                <ul className="list-disc list-inside ml-6 font-normal">
                                    <li>"Sales" of personal information</li>
                                    <li>Targeted advertising (in supported regions)</li>
                                </ul>
                            </li>
                            <li className="font-semibold">Learn more and enable GPC via supported browsers or extensions: globalprivacycontrol.org</li>
                        </ul>
                    </div>

                    <div>
                        <p className="font-semibold">Additional Controls</p>
                        <ul className="list-disc list-inside">
                            <li>You can also manage your privacy using:
                                <ul className="list-disc list-inside ml-6">
                                    <li>Marketing opt-outs (see previous section)</li>
                                    <li>Account settings to control data collection and use</li>
                                </ul>
                            </li>
                        </ul>
                    </div>

                    <div>
                        <p className="font-semibold">Data Portability</p>
                        <p>You may request a copy of your data in a portable format, allowing you to transfer it to another service provider.</p>
                    </div>
                    <div>
                        <p className="font-semibold">What You Can Request:</p>
                        <ul className="list-disc list-inside">
                            <li>An electronic file containing: Your basic account information</li>
                            <li>Data you've created in areas under your sole control (e.g., your personal Bitbucket repository)</li>
                        </ul>
                        <p>Not all data is eligible for portability—only specific types depending on context.</p>
                    </div>
                </div>
            </div>
            {/* Section 14 */}
            <div className='space-y-4 mb-4 w-full'>
                <h2 className="text-[#3A57FF] font-medium text-[26px]">International Data Transfers</h2>
                <div className="space-y-4 text-[17px]">
                    <div>
                        <p>We operate globally, which means your personal information may be transferred, processed, and stored outside your country of residence. We take steps to protect your data during these transfers.</p>
                        <p>Here's a streamlined and user-friendly summary of the international data transfer section, organized into digestible parts with clear headings and helpful links:</p>
                    </div>
                    <div>
                        <p className="font-semibold">International Data Transfers</p>
                        <p>We operate globally, which means your personal information may be transferred, processed, and stored outside your country of residence. We take steps to protect your data during these transfers.</p>
                    </div>

                    <div>
                        <p className="font-semibold">Transfers Within UBOARDS INNOVATIONS LTD Companies</p>
                        <p>To support global operations, your data may be accessed from countries where UBOARDS INNOVATIONS LTD-owned companies operate. These countries may not have the same privacy protections as your home country.</p>
                    </div>

                    <div>
                        <p className="font-semibold">To safeguard your data, UBOARDS INNOVATIONS LTD uses:</p>
                        <ul className="list-disc list-inside">
                            <li>EU-U.S. Data Privacy Framework (DPF)</li>
                            <li>Standard Contractual Clauses (SCCs) approved by the European Commission</li>
                            <li>UK and Swiss extensions to the DPF</li>
                        </ul>
                    </div>

                    <div>
                        <p className="font-semibold">Transfers to Third Parties</p>
                        <p>Some third-party service providers we work with are located in countries with different privacy laws. For users in the EU, UK, and Switzerland, we protect your data using:</p>
                        <ul className="list-disc list-inside">
                            <li>Standard Contractual Clauses (SCCs)</li>
                            <li>Binding Corporate Rules</li>
                            <li>Other legal safeguards</li>
                        </ul>
                    </div>

                    <div>
                        <p className="font-semibold">Data Privacy Frameworks</p>
                        <p>We comply with international data privacy frameworks that govern the transfer of personal data across borders. These frameworks ensure:</p>
                        <ul className="list-disc list-inside">
                            <li>Proper handling of personal data</li>
                            <li>Legal accountability for misuse</li>
                            <li>Oversight by regulatory bodies</li>
                            <li>Access to dispute resolution and arbitration</li>
                        </ul>
                    </div>

                    <div>
                        <p className="font-semibold">Dispute Resolution & Oversight</p>
                        <p>If you have unresolved concerns about how your data is handled:</p>
                        <ul className="list-disc list-inside">
                            <li>You may file a complaint with an independent dispute resolution provider</li>
                            <li>Services are free of charge</li>
                            <li>Regulatory bodies such as the Federal Trade Commission (FTC) oversee compliance</li>
                            <li>In certain cases, binding arbitration may be available</li>
                        </ul>
                    </div>

                    <div>
                        <p className="font-semibold">Legal Requests & Accountability</p>
                        <ul className="list-disc list-inside">
                            <li>Personal data may be disclosed to public authorities when legally required</li>
                            <li>For onward transfers to third parties, the original data handler remains accountable unless proven otherwise</li>
                        </ul>
                    </div>
                    <div>
                        <p className="font-semibold">International Data Transfers will be notified.</p>
                        <p>We operate globally, which means your personal information may be transferred, processed, and stored outside your country of residence. We take steps to protect your data during these transfers.</p>
                    </div>

                    <div>
                        <p className="font-semibold">Transfers Within UBOARDS INNOVATIONS LTD Companies</p>
                        <p>To support global operations, your data may be accessed from countries where UBOARDS INNOVATIONS LTD-owned companies operate. These countries may not have the same privacy protections as your home country.</p>
                    </div>

                    <div>
                        <p className="font-semibold">To safeguard your data, UBOARDS INNOVATIONS LTD uses:</p>
                        <ul className="list-disc list-inside">
                            <li>EU-U.S. Data Privacy Framework (DPF)</li>
                            <li>Standard Contractual Clauses (SCCs) approved by the European Commission</li>
                            <li>UK and Swiss extensions to the DPF</li>
                        </ul>
                    </div>

                    <div>
                        <p className="font-semibold">Transfers to Third Parties</p>
                        <p>Some third-party service providers we work with are located in countries with different privacy laws. For users in the EU, UK, and Switzerland, we protect your data using:</p>
                        <ul className="list-disc list-inside">
                            <li>Standard Contractual Clauses (SCCs)</li>
                            <li>Binding Corporate Rules</li>
                            <li>Other legal safeguards</li>
                        </ul>
                    </div>

                    <div>
                        <p className="font-semibold">Data Privacy Frameworks</p>
                        <p>We comply with international data privacy frameworks that govern the transfer of personal data across borders. These frameworks ensure:</p>
                        <ul className="list-disc list-inside">
                            <li>Proper handling of personal data</li>
                            <li>Legal accountability for misuse</li>
                            <li>Oversight by regulatory bodies</li>
                            <li>Access to dispute resolution and arbitration</li>
                        </ul>
                    </div>

                    <div>
                        <p className="font-semibold">Dispute Resolution & Oversight</p>
                        <p>If you have unresolved concerns about how your data is handled:</p>
                        <ul className="list-disc list-inside">
                            <li>You may file a complaint with an independent dispute resolution provider</li>
                            <li>Services are free of charge</li>
                            <li>Regulatory bodies such as the Federal Trade Commission (FTC) oversee compliance</li>
                            <li>In certain cases, binding arbitration may be available</li>
                        </ul>
                    </div>

                    <div>
                        <p className="font-semibold">Legal Requests & Accountability</p>
                        <ul className="list-disc list-inside">
                            <li>Personal data may be disclosed to public authorities when legally required</li>
                            <li>For onward transfers to third parties, the original data handler remains accountable unless proven otherwise</li>
                        </ul>
                    </div>
                </div>
            </div>
            {/* Section 15 */}
            <div className='space-y-4 mb-4 w-full'>
                <h2 className="text-[#3A57FF] font-medium text-[26px]">Other Important Privacy Information</h2>
                <div className="space-y-4 text-[17px]">
                    <div>
                        <p className="font-semibold">Notice to End Users</p>
                        <p>Many services are designed for use within organizations. If you access a service through an organization (such as your employer), that organization acts as the administrator and may control certain aspects of your account and data.</p>
                    </div>

                    <div>
                        <p className="font-semibold">What Administrator Can Do:</p>
                        <p>Administrators may have the ability to:</p>
                        <ul className="list-disc list-inside">
                            <li>Require password resets</li>
                            <li>Restrict, suspend, or terminate your access</li>
                            <li>Access and retain information in your account</li>
                            <li>Install or remove third-party integrations</li>
                            <li>Modify your profile or account details</li>
                            <li>Limit your ability to set up or delete content</li>
                        </ul>
                    </div>

                    <div>
                        <p className="font-semibold">Organizational Email Addresses</p>
                        <p>Even if your account isn't currently managed by an organization, using a work or organization-provided email address may allow that organization to assert control over your account in the future. You'll be notified if this happens.</p>
                    </div>

                    <div>
                        <p className="font-semibold">How to Retain Control</p>
                        <p>If you prefer to keep your account independent:</p>
                        <ul className="list-disc list-inside">
                            <li>Use a personal email address to order or access services</li>
                            <li>Describe your membership with any organization-managed groups or teams</li>
                            <li>Update your email address in your account settings (if administrative control hasn't yet been asserted)</li>
                        </ul>
                        <p>Once an administrator takes control of your account, you may need their approval to make changes to your account settings.</p>
                    </div>

                    <div>
                        <p className="font-semibold">Need Help?</p>
                        <p>For questions about privacy or account control, please contact your organization directly or refer to its internal policies.</p>
                        <p>Would you like help drafting a user guide for navigating organizational control or a template for requesting account independence?</p>
                    </div>
                </div>
            </div>

            {/* Section 16 */}
            <div className='space-y-4 mb-4 w-full'>
                <h2 className="text-[#3A57FF] font-medium text-[26px]">Sensitive Personal Information</h2>
                <div className="space-y-4 text-[17px]">
                    <div>
                        <p>We do not use or disclose sensitive personal data except as permitted under applicable laws.</p>
                    </div>

                    <div>
                        <p className="font-semibold">Data Retention</p>
                        <p>We retain personal information only as long as necessary to:</p>
                        <ul className="list-disc list-inside">
                            <li>Fulfill business purposes outlined in this policy</li>
                            <li>Comply with legal and recordkeeping requirements</li>
                            <li>Resolve inquiries or complaints</li>
                            <li>Maintain an ongoing relationship with you</li>
                        </ul>
                        <p>For more details, refer to the "How long we keep information" section of this policy.</p>
                    </div>

                    <div>
                        <p className="font-semibold">Exercising Your Rights</p>
                        <p>If you're a resident of certain jurisdictions (e.g., California), you may have additional rights under local laws such as the California Consumer Privacy Act (CCPA).</p>
                        <p>These rights may include:</p>
                        <ul className="list-disc list-inside">
                            <li>Requesting details about how your data is processed</li>
                            <li>Accessing specific estimates or pieces of personal data</li>
                            <li>Requesting deletion or correction of your data</li>
                        </ul>
                        <p>Visit the "How to access and control your information" section for instructions.</p>
                        <p>We will never discriminate against you for exercising your privacy rights. To protect your data, we may ask for identity verification before fulfilling requests. If we cannot verify your identity, we may not be able to proceed.</p>
                    </div>

                    <div>
                        <p className="font-semibold">Opting Out of Third-Party Disclosures</p>
                        <p>You may opt out of:</p>
                        <ul className="list-disc list-inside">
                            <li>"Sales" of personal information</li>
                            <li>"Sharing" for cross-context behavioral advertising</li>
                        </ul>
                        <p>You can do this by:</p>
                        <ul className="list-disc list-inside">
                            <li>Clicking the opt-out link provided</li>
                            <li>Using recognized privacy preference signals like the Global Privacy Control (GPC)</li>
                        </ul>
                        <p>We do not knowingly sell or share data of individuals under 16 years of age.</p>
                    </div>

                    <div>
                        <p className="font-semibold">Authorized Agents</p>
                        <p>You may designate an authorized agent to act on your behalf. To do so:</p>
                        <ul className="list-disc list-inside">
                            <li>Provide written permission to the agent</li>
                            <li>We may request a copy of this permission</li>
                            <li>We may also ask you to verify your identity and confirm the authorization directly</li>
                        </ul>
                    </div>

                    <div>
                        <p className="font-semibold">Children's Privacy</p>
                        <p>Our services are not intended for individuals under 18. We do not knowingly collect personal data from children under 18.</p>
                        <p>If you become aware that a child has provided personal information:</p>
                        <ul className="list-disc list-inside">
                            <li>Please contact the appropriate support team</li>
                            <li>We will take steps to delete the data promptly</li>
                        </ul>
                    </div>

                    <div>
                        <p className="font-semibold">Changes to This Privacy Policy</p>
                        <p>We may update this policy periodically. When changes occur:</p>
                        <ul className="list-disc list-inside">
                            <li>We'll post updates on this page</li>
                            <li>For significant changes, we'll provide prominent notices (e.g., homepage, login screens, or email alerts)</li>
                            <li>Archived versions will be available for review</li>
                        </ul>
                        <p>We encourage you to review the policy regularly to stay informed.</p>
                        <p>If you disagree with any updates, you may need to stop using the services and deactivate your account.</p>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default PrivacyPolicy;
